import {
  Box,
  Typography,
  Paper,
  MenuItem,
  Select,
  SelectChangeEvent,
  Button,
  Alert,
  Divider,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Card,
  CardContent,
  Snackbar,
} from '@mui/material';
import {
  Send as SendIcon,
  Preview as PreviewIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Devices as DevicesIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Application } from '../types/application';
import { biztalkTemplates } from '../../../../verify-functions/src/utils/biztalkTemplates';
import AlertSendHistory from './AlertSendHistory';
import STATUS_COLORS from '../../../constants/statusColors';
import type { TemplateType } from './orderDetail/types';
import { TEMPLATE_OPTIONS } from './orderDetail/constants';
import { formatDate, formatDeviceNames } from './orderDetail/utils';
import { sendAlimtalkPriceNotice } from '../../../utils/alerts';

interface Props {
  application: Application | null;
}



// 🔗 API 엔드포인트 (환경변수로 관리 권장)
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api-4iuywcitfq-uc.a.run.app';



// 🎨 로딩 스켈레톤 컴포넌트
const AlertDetailSkeleton = () => (
  <Box sx={{ p: 3 }}>
    <Skeleton variant="text" width="30%" height={32} sx={{ mb: 2 }} />
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Skeleton variant="text" width="60%" height={24} />
        <Skeleton variant="text" width="80%" height={20} sx={{ mt: 1 }} />
        <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
      </CardContent>
    </Card>
    <Skeleton variant="rectangular" height={56} sx={{ mb: 2, borderRadius: 2 }} />
    <Skeleton variant="rectangular" height={120} sx={{ mb: 2, borderRadius: 2 }} />
    <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 2 }} />
  </Box>
);

const AlertDetailPanel = ({ application }: Props) => {
  const [selectedType, setSelectedType] = useState<TemplateType | ''>('');
  const [repairReason, setRepairReason] = useState<string>('');
  const [returnTrackingNumber, setReturnTrackingNumber] = useState<string>('');
  const [sentResult, setSentResult] = useState<'success' | 'error' | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // 🔄 application 변경 시 상태 초기화
  useEffect(() => {
    if (!application) {
      setSelectedType('');
      setRepairReason('');
      setReturnTrackingNumber('');
      setSentResult(null);
      setErrorMessage('');
      return;
    }

    // 기존 저장된 차감 사유가 있으면 자동 입력
    if (application.deduction_reason) {
      setRepairReason(application.deduction_reason);
    }
  }, [application]);

  // 📝 템플릿 선택 핸들러
  const handleTemplateSelect = useCallback((e: SelectChangeEvent<string>) => {
    const newType = e.target.value as TemplateType;
    setSelectedType(newType);
    setSentResult(null);
    setErrorMessage('');
    
    // 사유가 필요하지 않은 템플릿 선택 시 초기화
    if (!['가격안내', '매입 불가'].includes(newType)) {
      setRepairReason('');
    } else if (newType === '가격안내' && application?.deduction_reason) {
      // 기존 차감 사유 자동 입력
      setRepairReason(application.deduction_reason);
    }
    
    // 반송장번호가 필요하지 않은 템플릿 선택 시 초기화
    if (!['매입 불가', '반송'].includes(newType)) {
      setReturnTrackingNumber('');
    }
  }, [application?.deduction_reason]);

  // 🔍 사유 변경 핸들러
  const handleRepairReasonChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setRepairReason(e.target.value);
    setErrorMessage(''); // 에러 메시지 초기화
  }, []);

  // 📦 반송장번호 변경 핸들러
  const handleReturnTrackingChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setReturnTrackingNumber(e.target.value);
    setErrorMessage(''); // 에러 메시지 초기화
  }, []);

  // 📊 공통 파라미터 생성 (메모이제이션)
  const commonParams = useMemo(() => {
    if (!application || !selectedType) return null;

    const deviceName = formatDeviceNames(application.devices);
    const applyDate = formatDate(application.created_at);

    return {
      application_id: application.id,
      user_name: application.user_name,
      phone_number: application.phone_number,
      device_name: deviceName,
      apply_date: applyDate,
      event: selectedType,
    };
  }, [application, selectedType]);

  // 📱 메시지 미리보기 생성 (메모이제이션)
  const previewMessage = useMemo(() => {
    if (!selectedType || !commonParams || !application) return '';

    const template = biztalkTemplates[selectedType];
    if (!template) return '';

    const baseParams = {
      userName: application.user_name,
      deviceName: formatDeviceNames(application.devices),
      applyDate: formatDate(application.created_at),
    };

    if (selectedType === '가격안내') {
      const priceParams = {
        ...baseParams,
        price: application.price_offer?.toLocaleString() || '0',
        application_id: application.id,
      };
      return template.getMessage(priceParams);
    }

    if (selectedType === '매입 불가') {
      const rejectParams = {
        ...baseParams,
        reason: repairReason || '[사유를 입력하세요]',
        returnTrackingNumber: returnTrackingNumber || '처리 중',
      };
      return template.getMessage(rejectParams);
    }

    if (selectedType === '반송') {
      const returnParams = {
        ...baseParams,
        returnTrackingNumber: returnTrackingNumber || '처리 중',
      };
      return template.getMessage(returnParams);
    }

    return template.getMessage(baseParams);
  }, [selectedType, commonParams, application, repairReason]);

  const perDevicePreviews = useMemo(() => {
    // 🆕 가격안내 단일 템플릿 체크
    if (!application || selectedType !== '가격안내') return [] as any[];
    const items = (application.devices || []).filter(d => d.status === '가격안내');
    return items.map(d => {
      // 🆕 단일 '가격안내' 템플릿 사용
      const event = '가격안내';
      const t = biztalkTemplates[event];
      const priceStr = (d.price_offer || 0).toLocaleString();
      // 🆕 차감사유 라인: 값이 있을 때만 표시
      const deductionReasonLine = d.deduction_reason?.trim() 
        ? `차감사유 : ${d.deduction_reason.trim()}` 
        : '';
      const msg = t?.getMessage({
        userName: application.user_name,
        deviceName: d.deviceModel,
        applyDate: formatDate(application.created_at),
        price: priceStr,
        application_id: application.id,
        device_id: d.id,
        // 🆕 가격안내 단일 템플릿용 파라미터
        grade: d.grade || '-',
        deductionReasonLine,
        applicationNumber: application.application_number?.toString() || application.id,
      }) || '';

      const errors: string[] = [];
      if (!t) errors.push('템플릿 누락');
      if (t?.buttons) {
        t.buttons.forEach(btn => {
          if (btn.type === 'WL') {
            if (!btn.url_mobile || !btn.url_pc) errors.push(`버튼 URL 누락: ${btn.name}`);
          }
        });
      } else {
        errors.push('버튼 정보 누락');
      }
      if (d.price_offer === null || d.price_offer === undefined) errors.push('검수금액 누락');

      return {
        deviceId: d.id,
        deviceModel: d.deviceModel,
        grade: d.grade,
        priceStr,
        event,
        message: msg,
        errors,
      };
    });
  }, [application, selectedType, repairReason]);

  // ✅ 폼 유효성 검사
  const isFormValid = useMemo(() => {
    if (!selectedType) return false;
    
    // 사유 입력이 필수인 템플릿들
    if ((selectedType === '매입 불가') && !repairReason.trim()) {
      return false;
    }
    
    // 반송장번호가 필수인 템플릿들
    if (selectedType === '반송' && !returnTrackingNumber.trim()) {
      return false;
    }
    
    return true;
  }, [selectedType, repairReason, returnTrackingNumber]);

  // 📤 전송 확인 다이얼로그 열기
  const handleSendClick = useCallback(() => {
    if (!isFormValid) {
      setErrorMessage('필수 항목을 모두 입력해주세요.');
      return;
    }
    
    // ✅ 비활성화된 템플릿 체크
    const selectedOption = TEMPLATE_OPTIONS.find(opt => opt.value === selectedType);
    if (selectedOption?.disabled) {
      setErrorMessage('해당 알림 서비스는 현재 준비 중입니다. 빠른 시일 내에 서비스를 제공하겠습니다.');
      return;
    }
    
    setConfirmDialog(true);
  }, [isFormValid, selectedType]);

  // 📨 실제 전송 처리
  const handleConfirmSend = useCallback(async () => {
    if (!commonParams || !application) return;

    setConfirmDialog(false);
    setIsSending(true);
    setErrorMessage('');

    try {
      const isPriceNoticeLocal = selectedType === '가격안내';

      // 전송 파라미터 구성
      let sendParams: any = { ...commonParams };
      if (!isPriceNoticeLocal) {
        const template = biztalkTemplates[selectedType as TemplateType];
        if (!template) throw new Error('템플릿을 찾을 수 없습니다.');
        sendParams = {
          ...commonParams,
          title: template.getTitle?.({
            userName: application.user_name,
            deviceName: formatDeviceNames(application.devices),
            applyDate: formatDate(application.created_at),
          }) || '',
          link: template.link || null,
        };
      }

      // 가격안내 템플릿 특수 처리
      if (selectedType === '가격안내') {
        sendParams.price = application.price_offer?.toLocaleString() || '0';
      }
       
       // 매입 불가 템플릿 특수 처리
       if (selectedType === '매입 불가') {
         sendParams.reason = repairReason.trim();
         sendParams.returnTrackingNumber = returnTrackingNumber.trim() || null;
       }
       
       // 반송 템플릿 특수 처리
       if (selectedType === '반송') {
         sendParams.returnTrackingNumber = returnTrackingNumber.trim();
       }

      console.log('📤 전송 파라미터:', sendParams);

      const response = isPriceNoticeLocal
        ? await (async () => {
            const result = await sendAlimtalkPriceNotice(application.id, false);
            return new Response(JSON.stringify(result), { status: 200 });
          })()
        : await fetch(`${API_BASE_URL}/api/alerts/send`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify(sendParams),
          });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: 서버 오류가 발생했습니다.`);
      }

      const result = await response.json();
      const isSuccess = result.message === '알림 전송 성공' || result.success === true;

      if (isSuccess) {
        setSentResult('success');
        setSnackbarOpen(true);
        // 성공 시 폼 초기화 (선택사항)
        // setSelectedType('');
        // setRepairReason('');
      } else {
        throw new Error(result.message || '알림 전송에 실패했습니다.');
      }

    } catch (error) {
      console.error('📨 전송 실패:', error);
      const errorMsg = error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.';
      setErrorMessage(errorMsg);
      setSentResult('error');
    } finally {
      setIsSending(false);
    }
  }, [commonParams, application, selectedType, repairReason]);

  // 🔄 재시도 핸들러
  const handleRetry = useCallback(() => {
    setSentResult(null);
    setErrorMessage('');
  }, []);

  // 📭 빈 상태 렌더링
  if (!application) {
    return (
      <Box 
        sx={{ 
          p: 4, 
          minHeight: 400, 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          textAlign: 'center'
        }}
        role="main"
        aria-label="신청자 선택 대기"
      >
        <PersonIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          신청자를 선택해 주세요
        </Typography>
        <Typography variant="body2" color="text.disabled">
          왼쪽 목록에서 알림을 보낼 신청자를 클릭하세요
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minHeight: 500,
      }}
      role="main"
      aria-label="알림 전송 패널"
    >
      {/* 📋 헤더 */}
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography
          variant="h6"
          fontWeight={800}
          sx={{ color: '#1976d2', letterSpacing: '-1px' }}
        >
          알림 전송
        </Typography>
        <Chip
          icon={<DevicesIcon />}
          label={`${application.devices?.length || 0}개 기기`}
          size="small"
          variant="outlined"
          color="primary"
        />
      </Box>

      {/* 👤 신청자 정보 카드 */}
      <Card 
        elevation={0}
        sx={{
          border: '1.5px solid #e0e7ef',
          borderRadius: 3,
          background: '#fbfbfc',
        }}
      >
        <CardContent sx={{ pb: '16px !important' }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <PersonIcon color="action" />
            <Typography variant="subtitle1" fontWeight={700}>
              {application.user_name}
            </Typography>
            <Chip
              label={application.status}
              size="small"
              sx={{
                ml: 'auto',
                bgcolor: STATUS_COLORS[application.status] || '#757575',
                color: '#fff',
                fontWeight: 600,
              }}
            />
          </Box>

          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {application.phone_number}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <DevicesIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" noWrap>
              {formatDeviceNames(application.devices)}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              신청일: {formatDate(application.created_at) || '날짜 없음'}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Divider sx={{ my: 1, borderColor: '#e0e7ef' }} />

      {/* 📝 템플릿 선택 */}
      <Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          알림 템플릿 선택
        </Typography>
        
        <Select
          fullWidth
          size="small"
          value={selectedType}
          onChange={handleTemplateSelect}
          displayEmpty
          sx={{
            borderRadius: 2,
            bgcolor: '#f7fafd',
            '& .MuiSelect-select': {
              fontWeight: 600,
            }
          }}
          MenuProps={{
            PaperProps: {
              sx: { 
                borderRadius: 2,
                maxHeight: 300,
              },
            },
          }}
          inputProps={{
            'aria-label': '알림 템플릿 선택',
          }}
        >
          <MenuItem value="" disabled>
            <em>템플릿을 선택하세요</em>
          </MenuItem>
          {TEMPLATE_OPTIONS.map((option) => (
            <MenuItem 
              key={option.value} 
              value={option.value}
              disabled={option.disabled}
            >
              <Box sx={{ opacity: option.disabled ? 0.6 : 1 }}>
                <Typography 
                  variant="body2" 
                  fontWeight={600}
                  color={option.disabled ? 'text.disabled' : 'text.primary'}
                >
                  {option.label}
                </Typography>
                <Typography 
                  variant="caption" 
                  color={option.disabled ? 'text.disabled' : 'text.secondary'}
                >
                  {option.description}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* 🔧 사유 입력 (매입 불가) */}
      {(selectedType === '매입 불가') && (
        <Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            <>❌ 매입 불가 사유 <span style={{ color: '#d32f2f' }}>*</span></>
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder={
              "매입이 불가능한 구체적인 사유를 입력하세요 (예: '기기 식별 불가', '심각한 파손', '액침 흔적' 등)"
            }
            value={repairReason}
            onChange={handleRepairReasonChange}
            multiline
            rows={3}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#ffebee',
                '&:hover': { 
                  bgcolor: '#ffebee' 
                },
                '&.Mui-focused': { 
                  bgcolor: '#ffebee' 
                }
              }
            }}
            error={!repairReason.trim()}
            helperText={!repairReason.trim() ? '사유 입력이 필수입니다' : `${repairReason.length}/200자`}
            inputProps={{
              maxLength: 200,
            }}
          />
        </Box>
      )}

      {/* 📦 반송장번호 입력 (매입 불가, 반송) */}
      {(selectedType === '매입 불가' || selectedType === '반송') && (
        <Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            📦 반송장번호 입력 {selectedType === '반송' ? (
              <span style={{ color: '#d32f2f' }}>*</span>
            ) : (
              <span style={{ color: '#666' }}>(선택사항)</span>
            )}
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="CJ대한통운 반송장번호를 입력하세요 (예: 123456789012)"
            value={returnTrackingNumber}
            onChange={handleReturnTrackingChange}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: selectedType === '반송' ? '#e8f5e8' : '#f3e5f5',
                '&:hover': { 
                  bgcolor: selectedType === '반송' ? '#e8f5e8' : '#f3e5f5' 
                },
                '&.Mui-focused': { 
                  bgcolor: selectedType === '반송' ? '#e8f5e8' : '#f3e5f5' 
                }
              }
            }}
            error={selectedType === '반송' && !returnTrackingNumber.trim()}
            helperText={
              selectedType === '반송' 
                ? (!returnTrackingNumber.trim() ? '반송장번호 입력이 필수입니다' : '반송 완료 시 고객에게 전달할 반송장번호')
                : "반송장번호가 없으면 '처리 중'으로 표시됩니다"
            }
          />
        </Box>
      )}

      {/* 👁️ 메시지 미리보기 */}
      {selectedType?.startsWith('가격안내') ? (
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={1} sx={{ background: 'linear-gradient(90deg, #e3f2fd 0%, #f3e5f5 100%)', p: 1.5, borderRadius: 2, border: '1px solid #e1e8ed' }}>
            <PreviewIcon color="action" />
            <Typography variant="subtitle2" fontWeight={800} sx={{ letterSpacing: '-0.2px' }}>
              디바이스별 메시지 미리보기 ({perDevicePreviews.length}건)
            </Typography>
          </Box>
          {perDevicePreviews.length === 0 ? (
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 2, border: '1px solid #ffe0b2' }}>
              <Typography variant="body2" color="text.secondary">
                가격안내 상태의 디바이스가 없습니다.
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              {perDevicePreviews.map(p => (
                <Paper key={p.deviceId} elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e1e8ed', bgcolor: 'rgba(250,252,255,0.9)', boxShadow: '0 4px 18px rgba(0,0,0,0.06)' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" fontWeight={800} sx={{ letterSpacing: '-0.2px' }}>
                      {p.deviceModel} · {p.priceStr}원
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Chip label={p.grade ? `${p.grade}등급` : '등급미정'} size="small" sx={{ height: 20, bgcolor: p.grade === 'A' ? '#10b981' : p.grade === 'B' ? '#3b82f6' : p.grade === 'C' ? '#f59e0b' : p.grade === 'D' ? '#ef4444' : '#90a4ae', color: '#fff' }} />
                      <Chip label={p.event} size="small" sx={{ bgcolor: '#1976d2', color: '#fff', height: 20 }} />
                    </Box>
                  </Box>
                  {p.errors.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      {p.errors.map((e: string, idx: number) => (
                        <Chip key={idx} label={e} color="error" size="small" sx={{ mr: 0.5 }} />
                      ))}
                    </Box>
                  )}
                  <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'white', border: '1px solid #e1e8ed', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: '#1565c0', lineHeight: 1.6 }}>
                      {p.message}
                    </Typography>
                  </Paper>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      ) : (
        previewMessage && (
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <PreviewIcon color="action" />
              <Typography variant="subtitle2" fontWeight={600}>
                메시지 미리보기
              </Typography>
            </Box>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                whiteSpace: 'pre-line',
                borderRadius: 2,
                background: '#f0f7ff',
                border: '1px solid #bbdefb',
                fontFamily: 'inherit',
              }}
            >
              <Typography variant="body2" sx={{ color: '#1565c0', lineHeight: 1.6 }}>
                {previewMessage}
              </Typography>
            </Paper>
          </Box>
        )
      )}

      {/* 🚨 에러 메시지 */}
      {errorMessage && (
        <Alert 
          severity="error" 
          sx={{ borderRadius: 2 }}
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={handleRetry}
              aria-label="다시 시도"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          }
        >
          {errorMessage}
        </Alert>
      )}

      {/* 📤 전송 버튼 */}
      <Button
        variant="contained"
        fullWidth
        size="large"
        onClick={handleSendClick}
        disabled={!isFormValid || isSending}
        startIcon={isSending ? <RefreshIcon className="rotating" /> : <SendIcon />}
        sx={{
          borderRadius: 2,
          py: 1.5,
          fontWeight: 700,
          fontSize: 16,
          letterSpacing: '-0.5px',
          background: isFormValid ? '#1976d2' : '#bdbdbd',
          boxShadow: isFormValid ? '0 4px 16px #1976d233' : 'none',
          transition: 'all 0.2s ease',
          '&:hover': {
            background: isFormValid ? '#1565c0' : '#bdbdbd',
            transform: isFormValid ? 'translateY(-1px)' : 'none',
            boxShadow: isFormValid ? '0 6px 20px #1976d240' : 'none',
          },
          '&:disabled': {
            color: '#fff',
          },
          '& .rotating': {
            animation: 'rotate 1s linear infinite',
          },
          '@keyframes rotate': {
            from: { transform: 'rotate(0deg)' },
            to: { transform: 'rotate(360deg)' },
          },
        }}
        aria-label={isSending ? '전송 중' : '알림톡 전송'}
      >
        {isSending ? '전송 중...' : '알림톡 전송'}
      </Button>

      {/* ✅ 성공 메시지 */}
      {sentResult === 'success' && (
        <Alert 
          severity="success" 
          sx={{ borderRadius: 2 }}
          icon={<CheckCircleIcon />}
        >
          <Typography fontWeight={600}>
            알림톡이 성공적으로 전송되었습니다!
          </Typography>
        </Alert>
      )}

      {/* 📊 전송 이력 */}
      <Box sx={{ mt: 2 }}>
        <AlertSendHistory applicationId={application.id} />
      </Box>

      {/* 🔔 확인 다이얼로그 */}
      <Dialog
        open={confirmDialog}
        onClose={() => setConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <SendIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>
              알림톡 전송 확인
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>{application.user_name}</strong>님에게 
            <strong> {TEMPLATE_OPTIONS.find(opt => opt.value === selectedType)?.label}</strong> 
            알림을 전송하시겠습니까?
          </Typography>
          
          {previewMessage && (
            <Paper sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                {previewMessage}
              </Typography>
            </Paper>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={() => setConfirmDialog(false)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            취소
          </Button>
          <Button
            onClick={handleConfirmSend}
            variant="contained"
            startIcon={<SendIcon />}
            sx={{ borderRadius: 2, ml: 1 }}
            autoFocus
          >
            전송하기
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🎊 성공 스낵바 */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity="success"
          sx={{ borderRadius: 2 }}
        >
          알림톡 전송이 완료되었습니다! 📱
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AlertDetailPanel;
